import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { loginUrl } from "@/auth/loginUrl";
import { useAsyncData } from "@/hooks/useAsyncData";
import { formatCents } from "@/lib/money";
import { formatDate, formatTime } from "@/lib/dates";
import PaymentModal from "@/components/PaymentModal";
import ErrorState from "@/components/ErrorState";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, MapPin, Clock, Ticket, Minus, Plus, ArrowLeft, Loader2, Lock } from "lucide-react";

function getVideoInfo(url) {
  if (!url) return null;
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) return {
    type: "youtube",
    embedUrl: `https://www.youtube.com/embed/${ytMatch[1]}?origin=${window.location.origin}`,
    thumbUrl: `https://img.youtube.com/vi/${ytMatch[1]}/hqdefault.jpg`,
    watchUrl: url,
  };
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return {
    type: "vimeo",
    embedUrl: `https://player.vimeo.com/video/${vimeoMatch[1]}`,
    thumbUrl: null,
    watchUrl: url,
  };
  return { type: "other", embedUrl: url, thumbUrl: null, watchUrl: url };
}

function VideoPlayer({ url }) {
  const [playing, setPlaying] = useState(false);
  const info = getVideoInfo(url);
  if (!info) return null;

  if (!playing && info.thumbUrl) {
    return (
      <div
        className="relative w-full h-full cursor-pointer group"
        onClick={() => setPlaying(true)}
      >
        <img src={info.thumbUrl} alt="Video thumbnail" className="w-full h-full object-cover rounded-xl" />
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors rounded-xl">
          <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center shadow-lg">
            <svg viewBox="0 0 24 24" fill="white" className="w-7 h-7 ml-1"><polygon points="5,3 19,12 5,21" /></svg>
          </div>
        </div>
      </div>
    );
  }

  return (
    <iframe
      src={info.embedUrl + (playing ? "&autoplay=1" : "")}
      title="Video"
      className="w-full h-full"
      allowFullScreen
      allow="autoplay; encrypted-media"
    />
  );
}

export default function EventDetail() {
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get("id");
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quantity, setQuantity] = useState(1);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(0);
  const [showPayment, setShowPayment] = useState(false);

  const { data: event, loading, error, reload } = useAsyncData(
    () => (eventId ? api.events.get(eventId) : Promise.reject(new Error("Missing event id"))),
    [eventId],
  );

  useEffect(() => {
    if (!eventId) navigate(createPageUrl("Events"));
  }, [eventId, navigate]);

  if (loading) return (
    <div className="flex justify-center items-center min-h-[70vh]">
      <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
    </div>
  );
  if (error || !event) return (
    <div className="max-w-2xl mx-auto px-4 py-16">
      <ErrorState error={error ?? new Error("Event not found.")} onRetry={reload} />
      <div className="text-center mt-6">
        <Link to={createPageUrl("Events")} className="text-indigo-600 font-medium hover:underline">Back to Events</Link>
      </div>
    </div>
  );

  const hasOptions = event.ticket_options && event.ticket_options.length > 0;
  const selectedOption = hasOptions ? event.ticket_options[selectedOptionIndex] : null;
  const activePriceCents = hasOptions ? (selectedOption?.price_cents || 0) : (event.price_cents || 0);
  const unlimited = !hasOptions && event.total_tickets == null;
  const activeTotal = hasOptions ? (selectedOption?.total_tickets || 0) : (event.total_tickets || 0);
  const activeSold = hasOptions ? (selectedOption?.tickets_sold || 0) : (event.tickets_sold || 0);
  const available = unlimited ? Infinity : Math.max(0, activeTotal - activeSold);
  const soldOut = available <= 0;
  const maxQuantity = unlimited ? 100 : available;

  const handleBuy = () => {
    if (!user) {
      navigate(loginUrl(window.location.pathname + window.location.search));
      return;
    }
    setShowPayment(true);
  };

  const handlePaymentSuccess = (order) => {
    navigate(createPageUrl("OrderConfirmation") + `?orderId=${order.id}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero - split layout */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-stretch">
          {/* Left: title / breadcrumb / category */}
          <div className="flex-1 flex flex-col justify-center px-8 py-10 md:py-12">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-gray-400 mb-5">
              <Link to={createPageUrl("Home")} className="hover:text-indigo-600 transition-colors">Home</Link>
              <span>/</span>
              <Link to={createPageUrl("Events")} className="hover:text-indigo-600 transition-colors">Events</Link>
              <span>/</span>
              <span className="text-gray-600 truncate max-w-[180px]">{event.title}</span>
            </nav>

            {/* Category badge */}
            {event.category && (
              <Badge className="self-start mb-3 bg-indigo-100 text-indigo-700 border-indigo-200 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                {event.category}
              </Badge>
            )}

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-4">{event.title}</h1>

            {/* Quick date/location line */}
            <div className="flex flex-wrap gap-4 text-sm text-gray-500">
              {event.start_date && (
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-4 w-4 text-indigo-400" />
                  {formatDate(event.start_date, "long")}
                  {event.end_date && event.end_date !== event.start_date && <> – {formatDate(event.end_date, "long")}</>}
                </span>
              )}
              {(event.venue_name || event.location) && (
                <span className="flex items-center gap-1.5">
                  <MapPin className="h-4 w-4 text-indigo-400" />
                  {event.venue_name || event.location}
                </span>
              )}
            </div>

            <div className="mt-6">
              <Link to={createPageUrl("Events")} className="inline-flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800 font-medium transition-colors">
                <ArrowLeft className="h-4 w-4" /> Back to Events
              </Link>
            </div>
          </div>

          {/* Right: event image at 1080:603 ratio, half width */}
          <div className="w-full md:w-1/2 flex-shrink-0">
            {event.image_url ? (
              <div className="w-full overflow-hidden" style={{aspectRatio: "1080/603"}}>
                <img src={event.image_url} alt={event.title} className="w-full h-full object-contain bg-black" />
              </div>
            ) : (
              <div className="w-full bg-gradient-to-br from-indigo-500 to-purple-600" style={{aspectRatio: "1080/603"}} />
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Details */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="font-bold text-xl mb-4 text-gray-900">About This Event</h2>
            <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">{event.description || "No description provided."}</p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="font-bold text-xl mb-5 text-gray-900">Event Details</h2>
            <div className="space-y-4">
              {event.start_date && (
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Calendar className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide">Date</p>
                    {event.end_date && event.end_date !== event.start_date ? (
                      <p className="font-semibold text-gray-800">{formatDate(event.start_date, "long")} – {formatDate(event.end_date, "long")}</p>
                    ) : (
                      <p className="font-semibold text-gray-800">{formatDate(event.start_date, "weekdayLong")}</p>
                    )}
                  </div>
                </div>
              )}
              {event.daily_times && event.daily_times.length > 0 && !event.same_time_all_days ? (
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Schedule</p>
                    <div className="space-y-1">
                      {event.daily_times.map((dt, i) => {
                        const dateStr = formatDate(dt.date, "weekdayShort");
                        return (
                          <p key={i} className="text-sm text-gray-800">
                            <span className="font-semibold">{dateStr}:</span>{" "}
                            {formatTime(dt.start_time)}{dt.end_time ? ` – ${formatTime(dt.end_time)}` : ""}
                          </p>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : event.start_time && (
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide">Time</p>
                    <p className="font-semibold text-gray-800">
                      {formatTime(event.start_time)}
                      {event.end_time ? ` – ${formatTime(event.end_time)}` : ""}
                    </p>
                  </div>
                </div>
              )}
              {(event.venue_name || event.location) && (
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    {event.venue_logo_url ? (
                      <img src={event.venue_logo_url} alt="Venue Logo" className="w-8 h-8 object-contain rounded" />
                    ) : (
                      <MapPin className="h-5 w-5 text-indigo-600" />
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide">Venue</p>
                    <p className="font-semibold text-gray-800">{event.venue_name || event.location}</p>
                    {event.venue_address && <p className="text-sm text-gray-500">{event.venue_address}</p>}
                    {(event.venue_city || event.venue_state || event.venue_zip) && (
                      <p className="text-sm text-gray-500">
                        {[event.venue_city, event.venue_state, event.venue_zip].filter(Boolean).join(", ")}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {event.video_embed_urls && event.video_embed_urls.length > 0 && (
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="font-bold text-xl mb-4 text-gray-900">Event Preview</h2>
              <div className="space-y-4">
                {event.video_embed_urls.map((url, i) => (
                  <div key={i} className="aspect-video rounded-xl overflow-hidden">
                    <VideoPlayer url={url} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Purchase Card */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl p-6 shadow-sm sticky top-24">

            {/* Ticket Options */}
            {hasOptions ? (
              <div className="mb-5">
                <label className="text-sm font-semibold text-gray-700 block mb-2">Select Ticket Type</label>
                <div className="space-y-2">
                  {event.ticket_options.map((opt, i) => {
                    const optAvail = (opt.total_tickets || 0) - (opt.tickets_sold || 0);
                    const optSoldOut = optAvail <= 0;
                    return (
                      <button
                        key={opt.id || i}
                        type="button"
                        disabled={optSoldOut}
                        onClick={() => { setSelectedOptionIndex(i); setQuantity(1); }}
                        className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all ${
                          selectedOptionIndex === i
                            ? "border-indigo-500 bg-indigo-50"
                            : "border-gray-200 hover:border-gray-300"
                        } ${optSoldOut ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-sm text-gray-900">{opt.name}</span>
                          <span className="font-bold text-indigo-600">{formatCents(opt.price_cents)}</span>
                        </div>
                        {opt.description && <p className="text-xs text-gray-500 mt-1">{opt.description}</p>}
                        {opt.valid_date && (
                          <p className="text-xs text-indigo-500 mt-1">{formatDate(opt.valid_date, "weekdayShort")}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">{optSoldOut ? "Sold out" : `${optAvail} left`}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <>
                <div className="text-4xl font-bold text-indigo-600 mb-1">{formatCents(event.price_cents)}</div>
                <p className="text-sm text-gray-400 mb-5">per ticket</p>
              </>
            )}

            <div className={`flex items-center justify-between rounded-xl px-4 py-3 mb-5 text-sm font-medium ${soldOut ? "bg-red-50 text-red-600" : available < 10 ? "bg-amber-50 text-amber-600" : "bg-green-50 text-green-700"}`}>
              <div className="flex items-center gap-2">
                <Ticket className="h-4 w-4" />
                {soldOut ? "Sold out" : "Available now"}
              </div>
              <span>{soldOut ? "0 left" : unlimited ? "Open" : `${available} left`}</span>
            </div>

            {!soldOut && (
              <div className="mb-5">
                <label className="text-sm font-medium text-gray-700 block mb-2">Quantity</label>
                <div className="flex items-center gap-4">
                  <button onClick={() => setQuantity(q => Math.max(1, q - 1))} className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors">
                    <Minus className="h-4 w-4 text-gray-600" />
                  </button>
                  <input
                    type="number"
                    min="1"
                    max={maxQuantity}
                    value={quantity}
                    onChange={e => {
                      const val = parseInt(e.target.value.replace(/[^0-9]/g, ""), 10);
                      if (!isNaN(val)) setQuantity(Math.min(maxQuantity, Math.max(1, val)));
                    }}
                    className="text-2xl font-bold w-16 text-center border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300"
                  />
                  <button onClick={() => setQuantity(q => Math.min(maxQuantity, q + 1))} className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors">
                    <Plus className="h-4 w-4 text-gray-600" />
                  </button>
                </div>
              </div>
            )}

            <div className="flex justify-between items-center text-sm pt-4 border-t border-gray-100 mb-5">
              <span className="text-gray-500">Total</span>
              <span className="text-xl font-bold text-gray-900">{formatCents(activePriceCents * quantity)}</span>
            </div>

            {event.admin_only && user?.role !== "admin" ? (
              <Button
                disabled
                className="w-full h-12 bg-gray-200 text-gray-500 text-base font-semibold rounded-xl cursor-not-allowed"
              >
                <Lock className="h-4 w-4 mr-2" /> Admin Purchases Only
              </Button>
            ) : (
              <Button
                onClick={handleBuy}
                disabled={soldOut}
                className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-base font-semibold rounded-xl"
              >
                {soldOut ? "Sold Out" : !user ? <><Lock className="h-4 w-4 mr-2" />Sign In to Buy</> : "Buy Now"}
              </Button>
            )}

            <p className="text-xs text-center text-gray-400 mt-3 flex items-center justify-center gap-1">
              <Lock className="h-3 w-3" /> Secured by Square
            </p>
          </div>
        </div>
      </div>

      {showPayment && (
        <PaymentModal
          event={event}
          quantity={quantity}
          selectedOption={selectedOption}
          activePriceCents={activePriceCents}
          onSuccess={handlePaymentSuccess}
          onClose={() => setShowPayment(false)}
          isAdmin={user?.role === "admin"}
        />
      )}
    </div>
  );
}